/**
 * Sistema de registro global para actualizaciones periódicas de componentes
 * 
 * Este sistema permite que múltiples componentes (DeviceCard, GroupCard, etc.)
 * se registren para recibir actualizaciones periódicas desde mapaDispositivos
 * usando un solo interval global, en lugar de tener un interval por componente.
 * 
 * Las actualizaciones en tiempo real siguen viniendo del eventBus.
 * Este sistema actúa como backup periódico cada 2 segundos.
 */

type UpdateCallback = () => void;
type ComponentType = 'DeviceCard' | 'GroupCard' | 'GroupDetailView' | 'GroupRelayButtons';

interface RegisteredComponent {
  id: string; // ID único del componente
  type: ComponentType;
  callback: UpdateCallback;
}

const registeredComponents = new Map<string, RegisteredComponent>();
let globalUpdateInterval: number | null = null;
let isUpdateInProgress = false; // Flag para evitar acumulación de ciclos
let currentCycleTimeout: number | null = null; // Timeout para detectar ciclos demorados
const MAX_CYCLE_DURATION = 1500; // Máximo tiempo permitido para un ciclo (ms)

/** Scroll de lista activo: no bombardear setState en tarjetas (Settings/Dashboard). */
let uiScrollActive = false;
let uiScrollIdleTimer: number | null = null;

/** Marca scroll de UI; los backups periódicos y cards pueden omitir updates. */
export const markUiScrolling = (idleMs = 220): void => {
  uiScrollActive = true;
  if (uiScrollIdleTimer !== null) {
    window.clearTimeout(uiScrollIdleTimer);
  }
  uiScrollIdleTimer = window.setTimeout(() => {
    uiScrollActive = false;
    uiScrollIdleTimer = null;
  }, idleMs);
};

export const isUiScrolling = (): boolean => uiScrollActive;

/**
 * Registrar un componente para recibir actualizaciones periódicas
 * @param id ID único del componente (ej: "DeviceCard-123" o "GroupCard-456")
 * @param type Tipo de componente
 * @param callback Función que se ejecutará periódicamente para actualizar el componente
 */
export const registerComponent = (
  id: string,
  type: ComponentType,
  callback: UpdateCallback
): void => {
  registeredComponents.set(id, { id, type, callback });
  
  // Iniciar el interval si es el primer componente registrado
  if (registeredComponents.size === 1) {
    startGlobalUpdateInterval();
  }
};

/**
 * Desregistrar un componente (cuando se desmonta)
 * @param id ID único del componente a desregistrar
 */
export const unregisterComponent = (id: string): void => {
  registeredComponents.delete(id);
  
  // Detener el interval si no hay más componentes registrados
  if (registeredComponents.size === 0) {
    stopGlobalUpdateInterval();
  }
};

/**
 * Iniciar el interval global que actualiza todos los componentes registrados
 */
const startGlobalUpdateInterval = (): void => {
  if (globalUpdateInterval) return; // Ya está corriendo
  
  // Interval de 2 segundos como backup del eventBus
  // Más lento que los 500ms anteriores, pero suficiente como respaldo
  globalUpdateInterval = window.setInterval(() => {
    // No competir con el scroll táctil: N setState en DeviceCards traba el hilo principal.
    if (uiScrollActive) {
      return;
    }

    // ✅ ESTRATEGIA MEJORADA: Si hay un ciclo en progreso cuando llega el tiempo del siguiente ciclo (2000ms después),
    // significa que el ciclo anterior lleva al menos 2000ms (más que MAX_CYCLE_DURATION de 1500ms)
    // Por lo tanto, siempre debemos cancelarlo y ejecutar el nuevo ciclo
    if (isUpdateInProgress) {
      // Si llegamos aquí, el ciclo anterior lleva al menos 2000ms (porque el intervalo es de 2000ms)
      // Esto siempre será > MAX_CYCLE_DURATION (1500ms), así que siempre cancelamos el anterior
      if (currentCycleTimeout) {
        window.clearTimeout(currentCycleTimeout);
        currentCycleTimeout = null;
      }
      // Marcar que el ciclo anterior fue cancelado
      isUpdateInProgress = false;
      console.warn(`[UpdateRegistry] Ciclo anterior demorado (>${MAX_CYCLE_DURATION}ms), cancelando y ejecutando nuevo ciclo`);
    }
    
    // Marcar que hay una actualización en progreso
    isUpdateInProgress = true;
    
    // ✅ TIMEOUT: Si el ciclo lleva más de MAX_CYCLE_DURATION, permitir que el siguiente ciclo se ejecute
    currentCycleTimeout = window.setTimeout(() => {
      // Si llegamos aquí, el ciclo lleva más de MAX_CYCLE_DURATION
      // Liberar el flag para permitir que el siguiente ciclo se ejecute
      isUpdateInProgress = false;
      currentCycleTimeout = null;
      console.warn(`[UpdateRegistry] Ciclo actual demorado más de ${MAX_CYCLE_DURATION}ms, permitiendo siguiente ciclo`);
    }, MAX_CYCLE_DURATION);
    
    try {
      // Actualizar todos los componentes registrados
      registeredComponents.forEach((component) => {
        // Verificar si el ciclo fue cancelado (por timeout o por nuevo ciclo)
        if (!isUpdateInProgress) {
          return; // Salir del forEach si el ciclo fue cancelado
        }
        
        try {
          component.callback();
        } catch (error) {
          // Silenciar errores para no interrumpir otros componentes
          console.warn(`[UpdateRegistry] Error actualizando ${component.type} ${component.id}:`, error);
        }
      });
    } finally {
      // Limpiar timeout si el ciclo terminó normalmente (antes del timeout)
      if (currentCycleTimeout) {
        window.clearTimeout(currentCycleTimeout);
        currentCycleTimeout = null;
      }
      // Liberar el flag solo si el ciclo terminó normalmente (no fue cancelado por timeout)
      if (isUpdateInProgress) {
        isUpdateInProgress = false;
      }
    }
  }, 2000); // Cada 2 segundos
};

/**
 * Detener el interval global
 */
const stopGlobalUpdateInterval = (): void => {
  if (globalUpdateInterval) {
    window.clearInterval(globalUpdateInterval);
    globalUpdateInterval = null;
  }
  // Limpiar timeout si existe
  if (currentCycleTimeout) {
    window.clearTimeout(currentCycleTimeout);
    currentCycleTimeout = null;
  }
  // Resetear flag
  isUpdateInProgress = false;
};

/**
 * Limpiar todos los registros (útil para testing o reset)
 */
export const clearAllRegistrations = (): void => {
  registeredComponents.clear();
  stopGlobalUpdateInterval();
};

/**
 * Obtener estadísticas del registro (útil para debugging)
 */
export const getRegistryStats = (): {
  totalComponents: number;
  byType: Record<ComponentType, number>;
  isIntervalActive: boolean;
} => {
  const byType: Record<ComponentType, number> = {
    DeviceCard: 0,
    GroupCard: 0,
    GroupDetailView: 0,
    GroupRelayButtons: 0,
  };
  
  registeredComponents.forEach((component) => {
    byType[component.type]++;
  });
  
  return {
    totalComponents: registeredComponents.size,
    byType,
    isIntervalActive: globalUpdateInterval !== null,
  };
};
